import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ExclamationTriangleIcon, ClockIcon, HandRaisedIcon, PauseIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Unified tilt triggers for both Check Yourself and Stress Breaks
const TILT_TRIGGERS = [
    { value: 'hunger', label: 'Hunger', emoji: '🍔', color: 'yellow', bgClass: 'bg-yellow-100 dark:bg-yellow-900/30', textClass: 'text-yellow-700 dark:text-yellow-300', borderClass: 'border-yellow-400 dark:border-yellow-600', description: 'Need food' },
    { value: 'fatigue', label: 'Fatigue', emoji: '😴', color: 'blue', bgClass: 'bg-blue-100 dark:bg-blue-900/30', textClass: 'text-blue-700 dark:text-blue-300', borderClass: 'border-blue-400 dark:border-blue-600', description: 'Tired, losing focus' },
    { value: 'frustrated', label: 'Frustrated', emoji: '😤', color: 'red', bgClass: 'bg-red-100 dark:bg-red-900/30', textClass: 'text-red-700 dark:text-red-300', borderClass: 'border-red-400 dark:border-red-600', description: 'Tilting, angry, bored' },
    { value: 'ego', label: 'Ego', emoji: '👑', color: 'purple', bgClass: 'bg-purple-100 dark:bg-purple-900/30', textClass: 'text-purple-700 dark:text-purple-300', borderClass: 'border-purple-400 dark:border-purple-600', description: 'Playing to prove something' },
    { value: 'big_win', label: 'Big Win', emoji: '🚀', color: 'green', bgClass: 'bg-green-100 dark:bg-green-900/30', textClass: 'text-green-700 dark:text-green-300', borderClass: 'border-green-400 dark:border-green-600', description: 'Won a big pot' },
    { value: 'big_loss', label: 'Big Loss', emoji: '💔', color: 'rose', bgClass: 'bg-rose-100 dark:bg-rose-900/30', textClass: 'text-rose-700 dark:text-rose-300', borderClass: 'border-rose-400 dark:border-rose-600', description: 'Lost a big pot' },
    { value: 'entitlement', label: 'Entitlement', emoji: '🤑', color: 'orange', bgClass: 'bg-orange-100 dark:bg-orange-900/30', textClass: 'text-orange-700 dark:text-orange-300', borderClass: 'border-orange-400 dark:border-orange-600', description: 'Feeling owed a win' },
    { value: 'apathy', label: 'Apathy', emoji: '😐', color: 'slate', bgClass: 'bg-slate-100 dark:bg-slate-900/30', textClass: 'text-slate-700 dark:text-slate-300', borderClass: 'border-slate-400 dark:border-slate-600', description: 'Checked out, don\'t care' },
];

// Format seconds to MM:SS
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Format seconds to HH:MM:SS
const formatLongTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * BreakEnforcement Component
 *
 * Handles the entire break enforcement system:
 * - Break status bar (persistent during session)
 * - Mandatory break enforcement
 * - Stress break triggering
 * - Recalibration checklist
 * - Audio prompts
 */
function BreakEnforcement({ sessionId, onSessionEnd, onBreakComplete }) {
    // State
    const [breakStatus, setBreakStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    // Modal states
    const [showMandatoryModal, setShowMandatoryModal] = useState(false);
    const [showStressModal, setShowStressModal] = useState(false);
    const [showChecklistModal, setShowChecklistModal] = useState(false);
    const [showExtensionModal, setShowExtensionModal] = useState(false);
    const [showHardStopModal, setShowHardStopModal] = useState(false);

    // Extension form
    const [extensionConditions, setExtensionConditions] = useState({
        mental_excellent: false,
        game_quality_strong: false,
        decision_clarity: false,
        physical_ok: false,
    });

    // Check Yourself state
    const [stateLogs, setStateLogs] = useState([]);
    const [showBreakPrompt, setShowBreakPrompt] = useState(false);
    const [lastLoggedTrigger, setLastLoggedTrigger] = useState(null);

    // Break timer state
    const [breakTimeRemaining, setBreakTimeRemaining] = useState(0);

    // Stress break form
    const [selectedStressors, setSelectedStressors] = useState([]);
    const [stackSize, setStackSize] = useState('');

    // Checklist form
    const [checklistForm, setChecklistForm] = useState({
        feels_calm_present: null,
        no_urge_to_win_back: null,
        will_not_make_curiosity_calls: null,
        second_guessed_decisions: null,
        made_light_ego_call: null,
        physically_compromised: null,
        // Hard-stop triggers (Section D)
        emotionally_drained: null,
        cannot_handle_bad_spot: null,
        urge_to_win_back: null,
        mentally_bargaining: null,
        fatigue_affecting_play: null,
        // Key question (Section E)
        trust_fold_cleanly: null,
        strategy_acknowledged: false,
    });

    // Escalation state
    const [escalationLevel, setEscalationLevel] = useState(0);
    const escalationTimerRef = useRef(null);

    // Audio refs
    const audioRef = useRef(null);

    // Polling interval ref
    const pollIntervalRef = useRef(null);

    // Fetch state logs for Check Yourself
    const fetchStateLogs = useCallback(async () => {
        if (!sessionId) return;

        try {
            const response = await axios.get(`/api/state-logs/?session=${sessionId}`);
            // Handle both paginated and direct array responses
            const logs = response.data.results || response.data;
            setStateLogs(Array.isArray(logs) ? logs : []);
        } catch (err) {
            console.error('Failed to fetch state logs:', err);
        }
    }, [sessionId]);

    // Fetch break status
    const fetchBreakStatus = useCallback(async () => {
        if (!sessionId) return;

        try {
            const response = await axios.get(`/api/break-status/?session=${sessionId}`);
            setBreakStatus(response.data);

            // Check if mandatory break is due
            if (response.data.mandatory_break_due && response.data.session_state === 'IN_SESSION') {
                setShowMandatoryModal(true);
            }

            // Update break timer if in break
            if (response.data.active_break && response.data.active_break.is_timer_complete === false) {
                setBreakTimeRemaining(response.data.active_break.time_remaining_seconds);
            }

            // Show checklist if break timer complete
            if (response.data.session_state === 'CHECKLIST') {
                setShowChecklistModal(true);
                setShowMandatoryModal(false);
                setShowStressModal(false);
            }

        } catch (err) {
            console.error('Failed to fetch break status:', err);
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    // Initial fetch and polling
    useEffect(() => {
        fetchBreakStatus();
        fetchStateLogs();

        // Poll every 5 seconds
        pollIntervalRef.current = setInterval(() => {
            fetchBreakStatus();
            fetchStateLogs();
        }, 5000);

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, [fetchBreakStatus, fetchStateLogs]);

    // Break timer countdown
    useEffect(() => {
        if (breakTimeRemaining > 0 && breakStatus?.session_state === 'BREAK_ACTIVE') {
            const timer = setInterval(() => {
                setBreakTimeRemaining(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        fetchBreakStatus(); // Refresh to show checklist
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [breakTimeRemaining, breakStatus?.session_state, fetchBreakStatus]);

    // Reset timer when session state changes to IN_SESSION (break completed)
    useEffect(() => {
        if (breakStatus?.session_state === 'IN_SESSION') {
            setBreakTimeRemaining(0);
        }
    }, [breakStatus?.session_state]);

    // Escalation timer for ignored mandatory breaks
    useEffect(() => {
        if (showMandatoryModal && breakStatus?.config?.ignore_escalation_minutes) {
            const escalationTime = breakStatus.config.ignore_escalation_minutes * 60 * 1000;

            escalationTimerRef.current = setTimeout(() => {
                setEscalationLevel(1);
                // Trigger escalation on backend
                if (breakStatus?.active_break?.id) {
                    axios.post(`/api/break-logs/${breakStatus.active_break.id}/trigger_escalation/`);
                }
            }, escalationTime);

            return () => {
                if (escalationTimerRef.current) {
                    clearTimeout(escalationTimerRef.current);
                }
            };
        }
    }, [showMandatoryModal, breakStatus]);

    // Start a mandatory break
    const startMandatoryBreak = async () => {
        try {
            const breakData = {
                session: sessionId,
                break_type: 'mandatory',
                break_duration_required: breakStatus?.config?.break_duration_minutes || 5,
            };
            // Add stack size if provided
            if (stackSize && parseFloat(stackSize) > 0) {
                breakData.stack_size_before = parseFloat(stackSize);
            }
            await axios.post('/api/break-logs/', breakData);

            setStackSize('');
            await fetchBreakStatus();
        } catch (err) {
            console.error('Failed to start break:', err);
        }
    };

    // Start a stress break
    const startStressBreak = async () => {
        if (selectedStressors.length === 0) return;

        try {
            const breakData = {
                session: sessionId,
                break_type: 'stress',
                stressors: selectedStressors,
                break_duration_required: breakStatus?.config?.break_duration_minutes || 5,
            };
            // Add stack size if provided
            if (stackSize && parseFloat(stackSize) > 0) {
                breakData.stack_size_before = parseFloat(stackSize);
            }
            await axios.post('/api/break-logs/', breakData);

            setSelectedStressors([]);
            setStackSize('');
            setShowStressModal(false);
            await fetchBreakStatus();
        } catch (err) {
            console.error('Failed to start stress break:', err);
        }
    };

    // Handle trigger tap in Check Yourself grid
    const handleTriggerTap = async (trigger) => {
        try {
            // Immediately log to state-logs
            await axios.post('/api/state-logs/', {
                session: sessionId,
                state_type: trigger.value,
            });

            // Refresh state logs
            await fetchStateLogs();

            // Show break prompt
            setLastLoggedTrigger(trigger);
            setShowBreakPrompt(true);
        } catch (err) {
            console.error('Failed to log trigger:', err);
        }
    };

    // Handle break prompt response
    const handleBreakPromptResponse = (takeBreak) => {
        if (takeBreak && lastLoggedTrigger) {
            // Pre-select this trigger for the stress break
            setSelectedStressors([lastLoggedTrigger.value]);
            setShowStressModal(true);
        }
        setShowBreakPrompt(false);
        setLastLoggedTrigger(null);
    };

    // Delete a state log
    const handleDeleteStateLog = async (logId) => {
        try {
            await axios.delete(`/api/state-logs/${logId}/`);
            await fetchStateLogs();
        } catch (err) {
            console.error('Failed to delete state log:', err);
        }
    };

    // Confirm away from table
    const confirmAway = async () => {
        if (!breakStatus?.active_break?.id) return;

        try {
            await axios.post(`/api/break-logs/${breakStatus.active_break.id}/confirm_away/`);
            setEscalationLevel(0);
            if (escalationTimerRef.current) {
                clearTimeout(escalationTimerRef.current);
            }
            await fetchBreakStatus();

            // Play audio prompt if enabled
            playAudioPrompt();
        } catch (err) {
            console.error('Failed to confirm away:', err);
        }
    };

    // Play audio prompt
    const playAudioPrompt = async () => {
        if (!breakStatus?.config?.enable_audio_prompts) return;

        try {
            const promptType = breakStatus.active_break?.break_type === 'stress' ? 'stress_break' : 'standard_break';
            const response = await axios.get('/api/audio-prompts/');
            const prompt = response.data.find(p => p.prompt_type === promptType);

            if (prompt?.audio_url && audioRef.current) {
                audioRef.current.src = prompt.audio_url;
                audioRef.current.volume = (breakStatus.config.audio_volume || 80) / 100;
                audioRef.current.play();
            }
        } catch (err) {
            console.error('Failed to play audio:', err);
        }
    };

    // Calculate outcome preview based on current checklist answers
    const calculateOutcomePreview = () => {
        const {
            feels_calm_present,
            no_urge_to_win_back,
            will_not_make_curiosity_calls,
            second_guessed_decisions,
            made_light_ego_call,
            physically_compromised,
            emotionally_drained,
            cannot_handle_bad_spot,
            urge_to_win_back,
            mentally_bargaining,
            fatigue_affecting_play,
            trust_fold_cleanly,
            strategy_acknowledged
        } = checklistForm;

        // SECTION D - HARD-STOP TRIGGERS (non-overridable - must end session)
        const hardStopTriggers = [
            { value: emotionally_drained, reason: 'Emotionally drained' },
            { value: cannot_handle_bad_spot, reason: 'Another bad spot would break me' },
            { value: urge_to_win_back, reason: 'Urge to win it back' },
            { value: mentally_bargaining, reason: 'Mentally bargaining with the rule' },
            { value: fatigue_affecting_play, reason: 'Fatigue affecting folds/calls' },
        ];

        for (const trigger of hardStopTriggers) {
            if (trigger.value === true) {
                return { outcome: 'hard_stop', reason: `HARD STOP: ${trigger.reason}`, isHardStop: true };
            }
        }

        // SECTION E - KEY QUESTION: Trust to fold cleanly
        if (trust_fold_cleanly === false) {
            return { outcome: 'hard_stop', reason: 'Cannot trust self to fold cleanly next orbit', isHardStop: true };
        }

        // Section B - Objective triggers (hard rules) - any "Yes" means end session (no extend option)
        const objectiveTriggersYes = second_guessed_decisions || made_light_ego_call || physically_compromised;
        if (objectiveTriggersYes) {
            return { outcome: 'end_session', reason: 'Objective trigger failed - session must end' };
        }

        // Section A - Mental signals - "No" to any of these is concerning
        const mentalSignals = [feels_calm_present, no_urge_to_win_back, will_not_make_curiosity_calls];
        const mentalNoCount = mentalSignals.filter(s => s === false).length;
        if (mentalNoCount >= 2) {
            // No more discretionary extend - must end session
            return { outcome: 'end_session', reason: 'Two or more mental signals failed - session must end' };
        }

        // Section C - Strategy must be acknowledged
        if (!strategy_acknowledged) {
            return { outcome: 'extend', reason: 'Acknowledge strategy to continue' };
        }

        // All checks passed
        return { outcome: 'return', reason: 'Safe to continue session' };
    };

    const outcomePreview = calculateOutcomePreview();

    // Submit checklist
    const submitChecklist = async (forceOutcome = null) => {
        if (!breakStatus?.active_break?.id) {
            alert('No active break found. Please try refreshing the page.');
            return;
        }

        try {
            // Submit checklist response
            const response = await axios.post('/api/checklist-responses/', {
                break_log: breakStatus.active_break.id,
                ...checklistForm,
            });

            // Use forced outcome if provided (user clicked End Session), otherwise use calculated
            const outcome = forceOutcome || response.data.outcome;

            // Complete the break with the outcome
            await axios.post(`/api/break-logs/${breakStatus.active_break.id}/complete/`, { outcome });

            // Reset checklist form
            setChecklistForm({
                feels_calm_present: null,
                no_urge_to_win_back: null,
                will_not_make_curiosity_calls: null,
                second_guessed_decisions: null,
                made_light_ego_call: null,
                physically_compromised: null,
                emotionally_drained: null,
                cannot_handle_bad_spot: null,
                urge_to_win_back: null,
                mentally_bargaining: null,
                fatigue_affecting_play: null,
                trust_fold_cleanly: null,
                strategy_acknowledged: false,
            });

            // Reset break timer state
            setBreakTimeRemaining(0);
            setShowChecklistModal(false);

            if (outcome === 'end_session') {
                // Trigger session end
                if (onSessionEnd) {
                    onSessionEnd();
                }
            } else if (outcome === 'extend') {
                // Refresh to get new timer
                await fetchBreakStatus();
            } else {
                // Returning to session - notify parent to refresh session data
                await fetchBreakStatus();
                if (onBreakComplete) {
                    onBreakComplete();
                }
            }

        } catch (err) {
            console.error('Failed to submit checklist:', err);
            alert('Failed to submit checklist. Please try again.');
        }
    };

    // Check if checklist can be submitted
    const canSubmitChecklist = () => {
        // For hard-stop outcomes, only need strategy acknowledgment
        if (outcomePreview.isHardStop || outcomePreview.outcome === 'end_session') {
            return checklistForm.strategy_acknowledged;
        }

        return (
            checklistForm.feels_calm_present !== null &&
            checklistForm.no_urge_to_win_back !== null &&
            checklistForm.will_not_make_curiosity_calls !== null &&
            checklistForm.second_guessed_decisions !== null &&
            checklistForm.made_light_ego_call !== null &&
            checklistForm.physically_compromised !== null &&
            checklistForm.emotionally_drained !== null &&
            checklistForm.cannot_handle_bad_spot !== null &&
            checklistForm.urge_to_win_back !== null &&
            checklistForm.mentally_bargaining !== null &&
            checklistForm.fatigue_affecting_play !== null &&
            checklistForm.trust_fold_cleanly !== null &&
            checklistForm.strategy_acknowledged
        );
    };

    // Request session extension
    const requestExtension = async () => {
        try {
            const response = await axios.post(`/api/sessions/${breakStatus.session_id}/request-extension/`, {
                conditions: extensionConditions
            });
            setShowExtensionModal(false);
            setExtensionConditions({
                mental_excellent: false,
                game_quality_strong: false,
                decision_clarity: false,
                physical_ok: false,
            });
            await fetchBreakStatus();
        } catch (err) {
            console.error('Failed to request extension:', err);
            alert(err.response?.data?.error || 'Failed to request extension');
        }
    };

    // Check if all extension conditions are met
    const allExtensionConditionsMet = Object.values(extensionConditions).every(v => v);

    if (loading || !breakStatus) {
        return null;
    }

    const {
        session_state, time_since_last_break_seconds, next_mandatory_break_seconds, config, active_break,
        session_duration_minutes, target_session_minutes, hard_stop_minutes, max_extension_minutes,
        approaching_target, past_target, at_hard_stop, past_max,
        extension_granted, extension_available, extension_blocked_reasons
    } = breakStatus;

    // Don't render anything if session ended
    if (session_state === 'SESSION_ENDED') {
        return null;
    }

    // Format duration as HH:MM
    const formatDuration = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}:${mins.toString().padStart(2, '0')}`;
    };

    return (
        <>
            {/* Hidden audio element */}
            <audio ref={audioRef} />

            {/* Session Duration Warning Banner */}
            {session_state === 'IN_SESSION' && (past_max || (at_hard_stop && !extension_granted)) && (
                <div className={`${past_max ? 'bg-red-600 animate-pulse' : 'bg-amber-600'} text-white px-4 py-4 mb-4 rounded-lg shadow-lg`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <ExclamationTriangleIcon className="h-8 w-8" />
                            <div>
                                <div className="font-bold text-lg">
                                    {past_max ? '🛑 MAXIMUM TIME REACHED' : '⚠️ HARD STOP REACHED'}
                                </div>
                                <div className="text-sm opacity-90">
                                    Session: {formatDuration(session_duration_minutes)} / {formatDuration(past_max ? max_extension_minutes : hard_stop_minutes)}
                                    {past_max && ' - END SESSION NOW'}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {at_hard_stop && !extension_granted && !past_max && (
                                extension_available ? (
                                    <button
                                        onClick={() => setShowExtensionModal(true)}
                                        className="bg-white text-amber-700 px-4 py-2 rounded-lg font-bold hover:bg-amber-50 transition-colors"
                                    >
                                        Request Extension
                                    </button>
                                ) : (
                                    <div className="text-sm bg-white/20 px-3 py-2 rounded-lg">
                                        Extension blocked
                                    </div>
                                )
                            )}
                            <button
                                onClick={() => setShowHardStopModal(true)}
                                className="bg-red-700 hover:bg-red-800 px-4 py-2 rounded-lg font-bold transition-colors"
                            >
                                END SESSION
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Break Status Bar - Always visible during active session */}
            {session_state === 'IN_SESSION' && (
                <div className={`text-white px-4 py-3 mb-4 rounded-lg shadow-lg ${
                    past_target ? 'bg-gradient-to-r from-amber-600 to-orange-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'
                }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            {/* Session Duration */}
                            <div className="flex items-center gap-2">
                                <ClockIcon className="h-5 w-5" />
                                <div>
                                    <div className="text-xs opacity-80">Session Time</div>
                                    <div className={`font-mono font-bold ${past_target ? 'text-yellow-200' : ''}`}>
                                        {formatDuration(session_duration_minutes)}
                                        <span className="text-xs opacity-70 ml-1">
                                            / {formatDuration(extension_granted ? max_extension_minutes : hard_stop_minutes)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <ExclamationTriangleIcon className="h-5 w-5" />
                                <div>
                                    <div className="text-xs opacity-80">Next break</div>
                                    <div className={`font-mono font-bold ${next_mandatory_break_seconds < 300 ? 'text-yellow-300' : ''}`}>
                                        {next_mandatory_break_seconds === 0 ? 'NOW' : formatLongTime(next_mandatory_break_seconds)}
                                    </div>
                                </div>
                            </div>

                            {extension_granted && (
                                <div className="flex items-center gap-1 bg-green-500/30 px-2 py-1 rounded text-xs font-medium">
                                    ✅ Extended
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => setShowStressModal(true)}
                            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg font-bold transition-colors shadow-md"
                        >
                            <HandRaisedIcon className="h-5 w-5" />
                            STRESS BREAK
                        </button>
                    </div>
                </div>
            )}

            {/* Check Yourself Grid - Always visible during active session */}
            {session_state === 'IN_SESSION' && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                Check Yourself
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Tap when you notice these
                            </p>
                        </div>
                        {stateLogs.length > 0 && (
                            <span className="px-2 py-1 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                                {stateLogs.length} logged
                            </span>
                        )}
                    </div>

                    {/* Trigger Grid */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                        {TILT_TRIGGERS.map(trigger => {
                            const count = stateLogs.filter(s => s.state_type === trigger.value).length;
                            return (
                                <button
                                    key={trigger.value}
                                    onClick={() => handleTriggerTap(trigger)}
                                    className={`relative p-3 rounded-lg border-2 border-dashed transition-all hover:border-solid ${trigger.bgClass} ${trigger.textClass} ${trigger.borderClass} hover:shadow-md`}
                                >
                                    <div className="text-xl mb-0.5">{trigger.emoji}</div>
                                    <div className="text-xs font-medium truncate">{trigger.label}</div>
                                    {count > 0 && (
                                        <span className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full">
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Recent Logs */}
                    {stateLogs.length > 0 && (
                        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Recent (tap × to delete):</p>
                            <div className="flex flex-wrap gap-2">
                                {stateLogs.slice(0, 5).map((log) => {
                                    const trigger = TILT_TRIGGERS.find(t => t.value === log.state_type);
                                    return (
                                        <span
                                            key={log.id}
                                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${trigger?.bgClass || 'bg-gray-100 dark:bg-gray-700'} ${trigger?.textClass || 'text-gray-700 dark:text-gray-300'} group`}
                                        >
                                            {trigger?.emoji} {new Date(log.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                            {log.notes && <span className="opacity-75 italic ml-1">- {log.notes}</span>}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteStateLog(log.id);
                                                }}
                                                className="ml-1 opacity-50 hover:opacity-100 transition-opacity"
                                                title="Delete this log"
                                            >
                                                <XMarkIcon className="h-3 w-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                                {stateLogs.length > 5 && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400 self-center">
                                        +{stateLogs.length - 5} more
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Break Prompt - After logging a trigger */}
            {showBreakPrompt && lastLoggedTrigger && (
                <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 pt-12 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-sm w-full p-6">
                        <div className="text-center mb-4">
                            <span className="text-4xl">{lastLoggedTrigger.emoji}</span>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mt-2">
                                Logged: {lastLoggedTrigger.label}
                            </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                            Take a stress break?
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleBreakPromptResponse(false)}
                                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium"
                            >
                                No, I'm OK
                            </button>
                            <button
                                onClick={() => handleBreakPromptResponse(true)}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold"
                            >
                                Yes, take break
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Break Active Banner */}
            {(session_state === 'BREAK_ACTIVE' || session_state === 'CHECKLIST') && active_break && (
                <div className={`${active_break.break_type === 'stress' ? 'bg-red-600' : 'bg-amber-600'} text-white px-4 py-4 mb-4 rounded-lg shadow-lg`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <PauseIcon className="h-8 w-8" />
                            <div>
                                <div className="font-bold text-lg">
                                    {active_break.break_type === 'stress' ? 'STRESS BREAK' : 'MANDATORY BREAK'} IN PROGRESS
                                </div>
                                {active_break.confirmed_away ? (
                                    <div className="text-sm opacity-90">
                                        {session_state === 'CHECKLIST'
                                            ? 'Complete the recalibration checklist to continue'
                                            : `Time remaining: ${formatTime(breakTimeRemaining)}`
                                        }
                                    </div>
                                ) : (
                                    <div className="text-sm">Confirm you are away from the table</div>
                                )}
                            </div>
                        </div>

                        {!active_break.confirmed_away && (
                            <button
                                onClick={confirmAway}
                                className="bg-white text-gray-900 px-6 py-3 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
                            >
                                I AM AWAY FROM TABLE
                            </button>
                        )}
                    </div>

                    {/* Break timer progress */}
                    {active_break.confirmed_away && session_state === 'BREAK_ACTIVE' && (
                        <div className="mt-4">
                            <div className="bg-white/20 rounded-full h-3 overflow-hidden">
                                <div
                                    className="bg-white h-full transition-all duration-1000"
                                    style={{
                                        width: `${100 - (breakTimeRemaining / (active_break.break_duration_required * 60) * 100)}%`
                                    }}
                                />
                            </div>
                            <div className="text-center mt-2 font-mono text-2xl font-bold">
                                {formatTime(breakTimeRemaining)}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Mandatory Break Modal */}
            {showMandatoryModal && !active_break && (
                <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-12 overflow-y-auto">
                    <div className={`bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 ${escalationLevel > 0 ? 'animate-pulse ring-4 ring-red-500' : ''}`}>
                        <div className="text-center">
                            <div className="mx-auto w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
                                <ExclamationTriangleIcon className="h-10 w-10 text-amber-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                MANDATORY BREAK NOW
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                You've been playing for {formatLongTime(time_since_last_break_seconds)}.
                                Take a {config?.break_duration_minutes || 5} minute break to recalibrate.
                            </p>

                            {escalationLevel > 0 && (
                                <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-lg mb-4 font-bold">
                                    You've been ignoring this break alert!
                                </div>
                            )}

                            {/* Stack Size Input */}
                            <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-left">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Current Stack Size
                                </label>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500 dark:text-gray-400">$</span>
                                    <input
                                        type="number"
                                        value={stackSize}
                                        onChange={(e) => setStackSize(e.target.value)}
                                        placeholder="How much do you have now?"
                                        className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                        min="0"
                                        step="1"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={async () => {
                                    await startMandatoryBreak();
                                    setShowMandatoryModal(false);
                                }}
                                disabled={!stackSize || parseFloat(stackSize) < 0}
                                className={`w-full py-4 rounded-lg font-bold text-lg transition-colors ${
                                    stackSize && parseFloat(stackSize) >= 0
                                        ? 'bg-amber-600 hover:bg-amber-700 text-white'
                                        : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                }`}
                            >
                                {stackSize && parseFloat(stackSize) >= 0 ? 'START BREAK' : 'Enter Stack to Continue'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Stress Break Modal */}
            {showStressModal && (
                <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-12 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
                        <div className="text-center mb-6">
                            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                                <HandRaisedIcon className="h-10 w-10 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                STRESS BREAK
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400">
                                Select what's affecting you:
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            {TILT_TRIGGERS.map(trigger => (
                                <button
                                    key={trigger.value}
                                    onClick={() => {
                                        setSelectedStressors(prev =>
                                            prev.includes(trigger.value)
                                                ? prev.filter(s => s !== trigger.value)
                                                : [...prev, trigger.value]
                                        );
                                    }}
                                    className={`p-4 rounded-lg border-2 transition-all ${
                                        selectedStressors.includes(trigger.value)
                                            ? `${trigger.borderClass} ${trigger.bgClass}`
                                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="text-2xl mb-1">{trigger.emoji}</div>
                                    <div className={`font-medium ${selectedStressors.includes(trigger.value) ? trigger.textClass : 'text-gray-900 dark:text-white'}`}>{trigger.label}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{trigger.description}</div>
                                </button>
                            ))}
                        </div>

                        {/* Stack Size Input */}
                        <div className="mb-6 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Current Stack Size
                            </label>
                            <div className="flex items-center gap-2">
                                <span className="text-gray-500 dark:text-gray-400">$</span>
                                <input
                                    type="number"
                                    value={stackSize}
                                    onChange={(e) => setStackSize(e.target.value)}
                                    placeholder="How much do you have now?"
                                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    min="0"
                                    step="1"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowStressModal(false);
                                    setSelectedStressors([]);
                                }}
                                className="flex-1 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={startStressBreak}
                                disabled={selectedStressors.length === 0 || !stackSize || parseFloat(stackSize) < 0}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold transition-colors"
                            >
                                {stackSize && parseFloat(stackSize) >= 0 ? 'START BREAK' : 'Enter Stack First'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Extension Request Modal */}
            {showExtensionModal && (
                <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-12 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                            ⏰ Request Session Extension
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center">
                            Extension to {formatDuration(max_extension_minutes)} requires ALL conditions to be true.
                        </p>

                        {extension_blocked_reasons && extension_blocked_reasons.length > 0 && (
                            <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg p-3 mb-4">
                                <p className="text-red-700 dark:text-red-300 font-medium text-sm mb-2">Extension blocked:</p>
                                <ul className="text-red-600 dark:text-red-400 text-xs space-y-1">
                                    {extension_blocked_reasons.map((reason, i) => (
                                        <li key={i}>• {reason}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="space-y-3 mb-6">
                            <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                extensionConditions.mental_excellent
                                    ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500'
                                    : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent'
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={extensionConditions.mental_excellent}
                                    onChange={(e) => setExtensionConditions({...extensionConditions, mental_excellent: e.target.checked})}
                                    className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">🧘 Mental State Excellent</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Calm, patient, no frustration</p>
                                </div>
                            </label>

                            <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                extensionConditions.game_quality_strong
                                    ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500'
                                    : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent'
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={extensionConditions.game_quality_strong}
                                    onChange={(e) => setExtensionConditions({...extensionConditions, game_quality_strong: e.target.checked})}
                                    className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">🎰 Game Quality Strong</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Weak players present, table clearly profitable</p>
                                </div>
                            </label>

                            <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                extensionConditions.decision_clarity
                                    ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500'
                                    : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent'
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={extensionConditions.decision_clarity}
                                    onChange={(e) => setExtensionConditions({...extensionConditions, decision_clarity: e.target.checked})}
                                    className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">🎯 Decision Clarity Intact</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">No second-guessing, no forced spots</p>
                                </div>
                            </label>

                            <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                extensionConditions.physical_ok
                                    ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500'
                                    : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent'
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={extensionConditions.physical_ok}
                                    onChange={(e) => setExtensionConditions({...extensionConditions, physical_ok: e.target.checked})}
                                    className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">💪 Physical State OK</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Not tired, not hungry, not emotionally drained</p>
                                </div>
                            </label>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowExtensionModal(false)}
                                className="flex-1 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={requestExtension}
                                disabled={!allExtensionConditionsMet}
                                className="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold transition-colors"
                            >
                                Grant Extension
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Hard Stop Modal - Confirm End Session */}
            {showHardStopModal && (
                <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-12 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
                        <div className="text-center">
                            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                                <ExclamationTriangleIcon className="h-10 w-10 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                {past_max ? 'SESSION OVER' : 'End Session?'}
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                {past_max
                                    ? 'Maximum session time reached. You must end now.'
                                    : `Session has reached ${formatDuration(session_duration_minutes)}. Time to wrap up.`
                                }
                            </p>

                            <div className="flex gap-3">
                                {!past_max && (
                                    <button
                                        onClick={() => setShowHardStopModal(false)}
                                        className="flex-1 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setShowHardStopModal(false);
                                        if (onSessionEnd) onSessionEnd();
                                    }}
                                    className={`${past_max ? 'w-full' : 'flex-1'} py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold transition-colors`}
                                >
                                    🛑 END SESSION
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Recalibration Checklist Modal */}
            {showChecklistModal && (
                <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 pt-12 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 my-8">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                            RECALIBRATION CHECKLIST
                        </h2>

                        {/* Section D: HARD-STOP TRIGGERS (Check first - non-overridable) */}
                        <div className="mb-6">
                            <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                <span className="bg-red-600 text-white px-2 py-1 rounded text-sm">🛑</span>
                                Hard-Stop Check (Be Honest)
                            </h3>
                            <p className="text-xs text-red-600 dark:text-red-400 mb-3">
                                Any "Yes" here = session over. No negotiation.
                            </p>

                            <ChecklistItem
                                label="Am I emotionally drained?"
                                value={checklistForm.emotionally_drained}
                                onChange={(v) => setChecklistForm(f => ({ ...f, emotionally_drained: v }))}
                                invertColors
                            />
                            <ChecklistItem
                                label="Would another bad spot break me?"
                                value={checklistForm.cannot_handle_bad_spot}
                                onChange={(v) => setChecklistForm(f => ({ ...f, cannot_handle_bad_spot: v }))}
                                invertColors
                            />
                            <ChecklistItem
                                label="Do I have an urge to win it back?"
                                value={checklistForm.urge_to_win_back}
                                onChange={(v) => setChecklistForm(f => ({ ...f, urge_to_win_back: v }))}
                                invertColors
                            />
                            <ChecklistItem
                                label="Am I mentally bargaining with this rule?"
                                value={checklistForm.mentally_bargaining}
                                onChange={(v) => setChecklistForm(f => ({ ...f, mentally_bargaining: v }))}
                                invertColors
                            />
                            <ChecklistItem
                                label="Is fatigue clearly affecting my folds/calls?"
                                value={checklistForm.fatigue_affecting_play}
                                onChange={(v) => setChecklistForm(f => ({ ...f, fatigue_affecting_play: v }))}
                                invertColors
                            />
                        </div>

                        {/* Section E: KEY QUESTION */}
                        <div className="mb-6">
                            <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                <span className="bg-amber-500 text-white px-2 py-1 rounded text-sm">⚡</span>
                                The Key Question
                            </h3>

                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4 mb-3">
                                <ChecklistItem
                                    label="If I face a tough raise next orbit, do I trust myself to fold cleanly?"
                                    value={checklistForm.trust_fold_cleanly}
                                    onChange={(v) => setChecklistForm(f => ({ ...f, trust_fold_cleanly: v }))}
                                />
                            </div>
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                                If "No" → end session. No "one more orbit."
                            </p>
                        </div>

                        {/* Section A: Mental Signals */}
                        <div className="mb-6">
                            <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded text-sm">A</span>
                                Mental Signals
                            </h3>

                            <ChecklistItem
                                label="I feel calm and present (not rushed)"
                                value={checklistForm.feels_calm_present}
                                onChange={(v) => setChecklistForm(f => ({ ...f, feels_calm_present: v }))}
                            />
                            <ChecklistItem
                                label="No urge to 'win it back' / prove something"
                                value={checklistForm.no_urge_to_win_back}
                                onChange={(v) => setChecklistForm(f => ({ ...f, no_urge_to_win_back: v }))}
                            />
                            <ChecklistItem
                                label="I will not make curiosity calls"
                                value={checklistForm.will_not_make_curiosity_calls}
                                onChange={(v) => setChecklistForm(f => ({ ...f, will_not_make_curiosity_calls: v }))}
                            />
                        </div>

                        {/* Section B: Objective Triggers */}
                        <div className="mb-6">
                            <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded text-sm">B</span>
                                Objective Triggers
                            </h3>

                            <ChecklistItem
                                label="Did I second-guess 2+ decisions in a row before this break?"
                                value={checklistForm.second_guessed_decisions}
                                onChange={(v) => setChecklistForm(f => ({ ...f, second_guessed_decisions: v }))}
                                invertColors
                            />
                            <ChecklistItem
                                label="Did I make a light/ego call I knew was thin?"
                                value={checklistForm.made_light_ego_call}
                                onChange={(v) => setChecklistForm(f => ({ ...f, made_light_ego_call: v }))}
                                invertColors
                            />
                            <ChecklistItem
                                label="Am I physically compromised AND cannot fix it now?"
                                value={checklistForm.physically_compromised}
                                onChange={(v) => setChecklistForm(f => ({ ...f, physically_compromised: v }))}
                                invertColors
                            />
                        </div>

                        {/* Section C: Strategy Reminder */}
                        <div className="mb-6">
                            <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded text-sm">C</span>
                                Strategy Reminder
                            </h3>

                            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-3 text-sm text-gray-700 dark:text-gray-300">
                                {config?.strategy_reminder || "ATTACK MODE is fine: value vs sticky pools. DEFENSE MODE is the leak: overcalling. Default to overfold vs strong aggression unless clear evidence of bluffing."}
                            </div>

                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={checklistForm.strategy_acknowledged}
                                    onChange={(e) => setChecklistForm(f => ({ ...f, strategy_acknowledged: e.target.checked }))}
                                    className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                />
                                <span className="text-gray-700 dark:text-gray-300 font-medium">
                                    I acknowledge: tighten defense; fold more vs strong aggression; no ego.
                                </span>
                            </label>
                        </div>

                        {/* Section D: Table Conduct Reminder */}
                        {config?.presession_instructions && (
                            <div className="mb-6">
                                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                    <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded text-sm">D</span>
                                    Table Conduct
                                </h3>
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-700 dark:text-amber-300 whitespace-pre-line">
                                    {config.presession_instructions}
                                </div>
                            </div>
                        )}

                        {/* Outcome Preview Banner */}
                        {checklistForm.strategy_acknowledged && (
                            <div className={`mb-4 p-4 rounded-lg ${
                                outcomePreview.isHardStop || outcomePreview.outcome === 'end_session'
                                    ? 'bg-red-100 dark:bg-red-900/30 border-2 border-red-500 dark:border-red-600'
                                    : outcomePreview.outcome === 'extend'
                                    ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700'
                                    : 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700'
                            }`}>
                                <div className={`font-bold text-sm ${
                                    outcomePreview.isHardStop || outcomePreview.outcome === 'end_session'
                                        ? 'text-red-700 dark:text-red-300'
                                        : outcomePreview.outcome === 'extend'
                                        ? 'text-amber-700 dark:text-amber-300'
                                        : 'text-green-700 dark:text-green-300'
                                }`}>
                                    {outcomePreview.isHardStop && '🛑 HARD STOP - SESSION MUST END'}
                                    {!outcomePreview.isHardStop && outcomePreview.outcome === 'end_session' && '🛑 END SESSION REQUIRED'}
                                    {outcomePreview.outcome === 'extend' && '⏸️ ACKNOWLEDGE STRATEGY TO CONTINUE'}
                                    {outcomePreview.outcome === 'return' && '✅ READY TO CONTINUE'}
                                </div>
                                <div className={`text-sm mt-1 ${
                                    outcomePreview.isHardStop || outcomePreview.outcome === 'end_session'
                                        ? 'text-red-600 dark:text-red-400'
                                        : outcomePreview.outcome === 'extend'
                                        ? 'text-amber-600 dark:text-amber-400'
                                        : 'text-green-600 dark:text-green-400'
                                }`}>
                                    {outcomePreview.reason}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="space-y-3">
                            {/* HARD STOP - Only end session option, no alternatives */}
                            {outcomePreview.isHardStop ? (
                                <button
                                    onClick={() => submitChecklist('end_session')}
                                    disabled={!checklistForm.strategy_acknowledged}
                                    className="w-full py-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold text-lg transition-colors"
                                >
                                    🛑 END SESSION NOW
                                </button>
                            ) : outcomePreview.outcome === 'end_session' ? (
                                /* Regular end session - no extend option anymore */
                                <button
                                    onClick={() => submitChecklist('end_session')}
                                    disabled={!checklistForm.strategy_acknowledged}
                                    className="w-full py-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold text-lg transition-colors"
                                >
                                    🛑 END SESSION
                                </button>
                            ) : outcomePreview.outcome === 'extend' ? (
                                <>
                                    <button
                                        onClick={() => submitChecklist()}
                                        disabled={!canSubmitChecklist()}
                                        className="w-full py-4 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold text-lg transition-colors"
                                    >
                                        ⏸️ EXTEND BREAK
                                    </button>
                                    <button
                                        onClick={() => submitChecklist('end_session')}
                                        disabled={!checklistForm.strategy_acknowledged}
                                        className={`w-full py-3 border-2 rounded-lg font-medium transition-colors ${
                                            checklistForm.strategy_acknowledged
                                                ? 'border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                : 'border-gray-300 text-gray-400 cursor-not-allowed opacity-50'
                                        }`}
                                    >
                                        End Session Instead
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => submitChecklist()}
                                        disabled={!canSubmitChecklist()}
                                        className="w-full py-4 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold text-lg transition-colors"
                                    >
                                        ✅ CONTINUE SESSION
                                    </button>
                                    <button
                                        onClick={() => submitChecklist('end_session')}
                                        disabled={!checklistForm.strategy_acknowledged}
                                        className={`w-full py-2 text-sm transition-colors ${
                                            checklistForm.strategy_acknowledged
                                                ? 'text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400'
                                                : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                        }`}
                                    >
                                        Or end session anyway
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// Checklist item component
function ChecklistItem({ label, value, onChange, invertColors = false }) {
    const getYesColor = () => invertColors
        ? 'bg-red-100 dark:bg-red-900/30 border-red-500 text-red-700 dark:text-red-300'
        : 'bg-green-100 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-300';

    const getNoColor = () => invertColors
        ? 'bg-green-100 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-300'
        : 'bg-red-100 dark:bg-red-900/30 border-red-500 text-red-700 dark:text-red-300';

    return (
        <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
            <span className="text-sm text-gray-700 dark:text-gray-300 pr-4">{label}</span>
            <div className="flex gap-2 flex-shrink-0">
                <button
                    onClick={() => onChange(true)}
                    className={`px-4 py-2 rounded-lg border-2 font-bold text-sm transition-all ${
                        value === true
                            ? getYesColor()
                            : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:border-gray-300'
                    }`}
                >
                    Yes
                </button>
                <button
                    onClick={() => onChange(false)}
                    className={`px-4 py-2 rounded-lg border-2 font-bold text-sm transition-all ${
                        value === false
                            ? getNoColor()
                            : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:border-gray-300'
                    }`}
                >
                    No
                </button>
            </div>
        </div>
    );
}

export default BreakEnforcement;
